/**
 * Component Design Tokens
 *
 * Component-specific token handling that uses the general token utilities.
 * This matches the SCSS core/components/_tokens.scss implementation.
 */

import { designTokenValue } from '../core/tokens';
import { CssValue } from '../core/types';
import { type ComponentName } from '../values/components';

/**
 * Component token extractor with type safety
 *
 * Matches SCSS: component($component-name, $properties...)
 *
 * @param componentName - The component name (must be a valid ComponentName)
 * @param properties - The property path parts
 * @returns CSS var() function for the component token
 */
export function component(componentName: ComponentName, ...properties: string[]): CssValue {
    const propertyPath = properties.join('-');
    const tokenName = `${componentName}-${propertyPath}`;

    return designTokenValue('component', tokenName);
}

/**
 * Example usage:
 *
 * ```typescript
 * import { component } from './components';
 *
 * // Direct API - matches SCSS exactly
 * const buttonHeight = component('action-button', 'height', 'default'); // ✅ Valid component name
 * const buttonPadding = component('icon-button', 'padding', 'horizontal'); // ✅ Valid component name
 * const cardBorder = component('card', 'border', 'radius'); // ✅ Valid component name
 *
 * // TypeScript will show error for invalid component names:
 * // const invalidComponent = component('invalid-component', 'height'); // ❌ Type error
 * ```
 */
